import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiMapPin, FiCalendar, FiClock, FiAlertCircle, FiPackage, FiPlus } from 'react-icons/fi';
import Navbar from '../components/Navbar';
import { matchAPI } from '../services/api';

const MyMatches = () => {
  const [activeTab, setActiveTab] = useState('joined');
  const [joinedMatches, setJoinedMatches] = useState([]);
  const [createdMatches, setCreatedMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [matchToDelete, setMatchToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    loadMatches();
  }, []);

  const loadMatches = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      if (!user?._id) {
        setError('User not found');
        return;
      }

      // Get all matches and filter by participation and creation
      const response = await matchAPI.getAllMatches();
      if (response.success) {
        const allMatches = response.matches;
        
        // Matches where user is a participant
        const currentUserId = user._id?.toString();

        const joined = allMatches.filter((match) =>
          match.participants?.some((p) => {
            const participantId = p?._id?.toString() || p?.toString();
            return participantId === currentUserId;
          })
        );

        const created = allMatches.filter(
          (match) => match.createdBy?._id?.toString() === currentUserId
        );

        setJoinedMatches(joined);
        setCreatedMatches(created);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (matchId) => {
    setMatchToDelete(matchId);
  };

  const confirmDeleteMatch = async () => {
    if (!matchToDelete) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const response = await matchAPI.deleteMatch(matchToDelete);
      if (response.success) {
        setCreatedMatches(prev => prev.filter(m => m._id !== matchToDelete));
        setJoinedMatches(prev => prev.filter(m => m._id !== matchToDelete));
        setMatchToDelete(null);
      }
    } catch (err) {
      setDeleteError(err.message || 'Failed to cancel match. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setMatchToDelete(null);
    setDeleteError('');
  };

  const matchesToDisplay = activeTab === 'joined' ? joinedMatches : createdMatches;

  const getStatusColor = (status) => {
    switch (status) {
      case 'upcoming':
        return 'text-green-400';
      case 'completed':
        return 'text-gray-400';
      case 'cancelled':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="bg-black min-h-screen">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header Section */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-white tracking-wider flex items-center gap-3 mb-2">
            <FiPackage className="text-red-500" />
            MY MATCHES
          </h1>
          <p className="text-gray-400 text-sm uppercase tracking-widest">Manage your joined and created matches</p>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-8 bg-red-500/10 border-l-4 border-red-500 p-6 rounded-sm">
            <div className="flex items-center gap-3">
              <FiAlertCircle className="text-red-500 w-5 h-5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-red-500 text-sm uppercase tracking-widest">Error Loading Matches</h3>
                <p className="text-gray-400 text-xs mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-4 mb-12 border-b border-gray-800 pb-4">
          <button
            onClick={() => setActiveTab('joined')}
            className={`px-6 py-3 font-bold text-sm uppercase tracking-widest transition-all relative ${
              activeTab === 'joined'
                ? 'text-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Joined Matches
            {activeTab === 'joined' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 rounded-full"></div>
            )}
          </button>
          <button
            onClick={() => setActiveTab('created')}
            className={`px-6 py-3 font-bold text-sm uppercase tracking-widest transition-all relative ${
              activeTab === 'created'
                ? 'text-red-500'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            Created Matches
            {activeTab === 'created' && (
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-500 rounded-full"></div>
            )}
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin mb-6" />
            <p className="text-gray-400 font-bold uppercase tracking-widest text-sm text-center">
              Loading your matches...<br/>
              <span className="text-[10px] opacity-50">Syncing with PlayConnect servers</span>
            </p>
          </div>
        )}

        {/* Matches Grid */}
        {!loading && (
          <>
            {matchesToDisplay.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 bg-gray-900/30 border border-dashed border-gray-800 rounded-sm">
                <FiPackage className="w-12 h-12 text-gray-600 mb-4" />
                <p className="text-gray-400 font-bold uppercase tracking-widest text-sm mb-2">No {activeTab} matches</p>
                <p className="text-gray-500 text-xs mb-6">
                  {activeTab === 'joined' 
                    ? 'Browse matches in the Discover page to join one'
                    : 'Create a new match to get started'}
                </p>
                <Link
                  to={activeTab === 'joined' ? '/discover' : '/create-match'}
                  className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded transition text-xs uppercase tracking-widest"
                >
                  <FiPlus className="w-4 h-4" />
                  {activeTab === 'joined' ? 'Browse Matches' : 'Create Match'}
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {matchesToDisplay.map((match) => (
                  <div
                    key={match._id}
                    className="bg-gray-900 border-l-4 border-red-500 p-6 rounded-sm hover:shadow-lg hover:shadow-red-500/10 transition-all duration-300 flex flex-col h-full"
                  >
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <span
                        className={`text-xs font-bold uppercase tracking-widest px-3 py-1 rounded ${getStatusColor(
                          match.status
                        )} bg-gray-800`}
                      >
                        {match.status}
                      </span>
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                        {match.sport}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-white font-bold text-lg mb-4 uppercase tracking-wide">
                      {match.title}
                    </h3>

                    {/* Location */}
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-3">
                      <FiMapPin className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="uppercase tracking-wide">{match.location}</span>
                    </div>

                    {/* Date/Time */}
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-6 pb-6 border-b border-gray-800">
                      <FiCalendar className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <span className="text-xs">
                        {match.date && new Date(match.date).toLocaleDateString()} 
                        {match.time && ` • ${match.time}`}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 items-center mt-auto">
                      {activeTab === 'created' && (
                        <button 
                          onClick={() => handleDeleteClick(match._id)}
                          className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-500 font-bold py-2 px-3 rounded text-xs transition uppercase tracking-wider border border-red-500/50"
                        >
                          Cancel
                        </button>
                      )}
                      <Link
                        to={`/match/${match._id}`}
                        className={`${activeTab === 'created' ? 'flex-1' : ''} text-center bg-gray-800 hover:bg-gray-700 text-white font-bold py-2 px-3 rounded text-xs transition uppercase tracking-wider`}
                      >
                        Details
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Red Line */}
      <div className="h-1 bg-gradient-to-r from-red-500 via-red-500 to-transparent mt-16"></div>

      {/* Delete Confirmation Modal */}
      {matchToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
          <div className="bg-gray-900 border-t-4 border-red-500 p-8 rounded-sm max-w-sm w-full shadow-2xl shadow-red-500/20 animate-in fade-in zoom-in-95 duration-300">
            <h3 className="text-xl font-bold text-white mb-2 uppercase tracking-widest">Cancel Match</h3>
            <p className="text-gray-400 text-sm mb-6 leading-relaxed">
              Are you sure you want to cancel this match? This action cannot be undone.
            </p>
            {deleteError && (
              <div className="bg-red-500/10 border-l-4 border-red-500 rounded p-4 mb-6">
                <p className="text-red-400 text-xs font-bold uppercase tracking-widest">{deleteError}</p>
              </div>
            )}
            <div className="flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                disabled={isDeleting}
                className="px-6 py-2 text-sm font-bold text-gray-400 hover:text-white transition disabled:opacity-50 uppercase tracking-wider"
              >
                Keep Match
              </button>
              <button
                onClick={confirmDeleteMatch}
                disabled={isDeleting}
                className="px-6 py-2 text-sm font-bold bg-red-500 text-white rounded hover:bg-red-600 transition disabled:opacity-50 uppercase tracking-widest"
              >
                {isDeleting ? 'CANCELING...' : 'YES, CANCEL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyMatches;
